import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { QueryClientProvider } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { type ReactNode, useState } from 'react';

import { createQueryClient } from './query-client';

/**
 * Wraps the app in a single QueryClient. `useState` keeps one client per mount
 * (a fresh one per SSR render, stable across client re-renders), so server and
 * browser never share cache state.
 *
 * Pass `persistKey` to persist the cache to localStorage (client-only): on the next
 * load the cache is restored synchronously, so the shell and its data paint from the
 * last-known state instead of a spinner, then revalidate in the background
 * (stale-while-revalidate). `buster` (the build id) discards a cache written by an
 * older build so a shape change can't hydrate stale data. Only successful queries are
 * persisted, and entries older than a day are dropped.
 */
export function QueryProvider({
	children,
	onError,
	persistKey,
	buster,
}: {
	children: ReactNode;
	onError?: (error: unknown) => void;
	persistKey?: string;
	buster?: string;
}) {
	const [client] = useState(() => createQueryClient(onError));
	const [persister] = useState(() =>
		persistKey && typeof window !== 'undefined'
			? createSyncStoragePersister({ storage: window.localStorage, key: persistKey })
			: null,
	);

	if (persister) {
		return (
			<PersistQueryClientProvider
				client={client}
				persistOptions={{
					persister,
					buster,
					maxAge: 1000 * 60 * 60 * 24,
					dehydrateOptions: {
						shouldDehydrateQuery: (query) => query.state.status === 'success',
					},
				}}
			>
				{children}
			</PersistQueryClientProvider>
		);
	}

	return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}

/**
 * Drop a persisted cache from localStorage (client-only, no-op on the server).
 * Call on sign-out and on any principal change (workspace switch) so the next
 * visitor on a shared browser can't restore the previous user's data — clearing
 * the in-memory QueryClient alone leaves the persisted copy on disk, which is
 * restored synchronously on the next load.
 */
export function clearPersistedQueryCache(persistKey: string): void {
	if (typeof window !== 'undefined') {
		window.localStorage.removeItem(persistKey);
	}
}
